import { getApi } from "~/auth";
import UI from "~/ui";
import { t } from "~/hooks/useI18n";
import { subscribeNewsletterNoConfirm, unsubscribeNewsletter } from "~/mailup";

/**
 * Modello per un consenso da salvare
 */
export interface ConsensoData {
    consensoId: number;
    codice: string;
    checked: boolean;
}

/**
 * Helper per la gestione centralizzata dei consensi privacy
 */
export class ConsensiHelper {

    /**
     * Raccoglie i consensi da un form contenente checkbox con attributi data-privacy-id e data-codice
     * @param formElement Elemento del form (HTMLElement o jQuery selector)
     * @returns Array di consensi con id, codice e stato
     */
    static collectConsensiFromForm(formElement: HTMLElement | string): ConsensoData[] {
        const consensi: ConsensoData[] = [];
        const $form = typeof formElement === 'string' ? $(formElement) : $(formElement);
        console.info("Consensi Form :" + formElement);
        $form.find('input[type="checkbox"].form-check-input').each(function () {
            const checkbox = $(this);
            const consensoId = parseInt(checkbox.attr('data-privacy-id') || '0');
            const codice = checkbox.attr('data-codice') || '';
            const checked = checkbox.prop('checked') as boolean;

            console.info("Id: " + consensoId + ", codice: " + codice + ", concesso: " + checked);
            if (consensoId > 0) {
                consensi.push({
                    consensoId: consensoId,
                    codice: codice,
                    checked: checked
                });
            }
        });

        return consensi;
    }

    /**
     * Salva i consensi sul server tramite API
     * @param consensi Array di consensi da salvare
     * @returns Promise che si risolve quando il salvataggio è completato
     */
    static async saveConsensi(consensi: ConsensoData[]): Promise<void> {
        await getApi().fetchWithAuth("PUT /privacy/consensiUtente", {
            body: JSON.stringify({
                consensi: consensi.map(c => ({
                    consensoId: c.consensoId,
                    checked: c.checked
                }))
            })
        });
    }

    /**
     * Gestisce la sincronizzazione con MailUp per il consenso newsletter
     * @param consensi Array di consensi (deve contenere il consenso NEWS)
     * @param userEmail Email dell'utente per l'iscrizione/disiscrizione
     */
    static async syncNewsletterConsent(consensi: ConsensoData[], userEmail: string): Promise<void> {
        const consensoNews = consensi.find(c => c.codice === "NEWS");

        if (!consensoNews) {
            return; // Nessun consenso NEWS da gestire
        }

        if (!userEmail) {
            console.warn("⚠️ Email utente non disponibile per gestione newsletter");
            return;
        }

        if (!window.ATP_ListID_Newsletter || !window.ATP_GroupId_Registrazione) {
            console.warn("⚠️ Configurazione MailUp non trovata");
            return;
        }

        try {
            if (consensoNews.checked) {
                // ✅ ISCRIZIONE alla newsletter
                console.info("📧 Iscrizione newsletter per:", userEmail);

                await subscribeNewsletterNoConfirm(
                    window.ATP_ListID_Newsletter,
                    window.ATP_GroupId_Registrazione,
                    userEmail
                );

                console.info("✅ Iscrizione newsletter completata");

            } else {
                // ✅ DISISCRIZIONE dalla newsletter
                console.info("📧 Disiscrizione newsletter per:", userEmail);

                await unsubscribeNewsletter(
                    window.ATP_ListID_Newsletter,
                    userEmail
                );

                console.info("✅ Disiscrizione newsletter completata");
            }

        } catch (error) {
            console.error("❌ Errore gestione newsletter:", error);

            const action = consensoNews.checked ? "iscrizione" : "disiscrizione";
            const errorMsg = t(`messages.error_${action}_newsletter`) ||
                `Errore durante ${action === 'iscrizione' ? "l'iscrizione" : "la disiscrizione"} alla newsletter`;

            UI.confirmOneButton("", errorMsg, "OK");
            throw error; // Rilancia per permettere al chiamante di gestirlo
        }
    }

    /**
     * Salva i consensi e sincronizza con newsletter in un'unica operazione
     * @param formElement Form da cui raccogliere i consensi
     * @param userEmail Email dell'utente autenticato
     * @param options Opzioni aggiuntive (es. skipNewsletter per non gestire MailUp)
     */
    static async saveAndSyncConsensi(
        formElement: HTMLElement | string,
        userEmail: string,
        options?: { skipNewsletter?: boolean }
    ): Promise<void> {
        // 1. Raccogli i consensi dal form
        const consensi = this.collectConsensiFromForm(formElement);

        if (consensi.length === 0) {
            console.warn("⚠️ Nessun consenso trovato nel form");
            return;
        }

        // 2. Salva i consensi sul server
        await this.saveConsensi(consensi);

        // 3. Sincronizza newsletter (se richiesto)
        if (!options?.skipNewsletter && userEmail) {
            await this.syncNewsletterConsent(consensi, userEmail);
        }
    }

    /**
     * Recupera lo stato di un consenso specifico da un form
     * @param formElement Form da cui cercare
     * @param codiceConsenso Codice del consenso (es. "NEWS", "PRIV", ecc.)
     * @returns true se il consenso è spuntato, false altrimenti
     */
    static getConsensoStatus(formElement: HTMLElement | string, codiceConsenso: string): boolean {
        const $form = typeof formElement === 'string' ? $(formElement) : $(formElement);
        const $checkbox = $form.find(`input[data-codice="${codiceConsenso}"]`);

        return $checkbox.length > 0 ? $checkbox.prop('checked') as boolean : false;
    }
}